import type * as React from "react";

/**
 * Props for a table row that navigates to a detail page on click. Rows were
 * previously `<tr onClick>` with no keyboard or screen-reader affordance —
 * mouse-only. This makes the row a focusable `role="link"` target that also
 * activates on Enter/Space, with a visible focus ring, while preserving the
 * existing click-anywhere behavior for mouse users.
 *
 * Usage: `<tr {...interactiveRowProps(() => navigate(...), \`Open \${name}\`)}>`.
 * Interactive children inside the row (links, buttons) must still call
 * `e.stopPropagation()` so they don't also trigger the row navigation.
 */
export function interactiveRowProps(onActivate: () => void, label: string) {
  return {
    role: "link",
    tabIndex: 0,
    "aria-label": label,
    onClick: onActivate,
    onKeyDown: (e: React.KeyboardEvent<HTMLTableRowElement>) => {
      // Space scrolls by default; Enter/Space both activate a link/button.
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onActivate();
      }
    },
    className:
      "cursor-pointer border-b border-border transition-colors last:border-0 hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
  } as const;
}
